import { api } from '@/services/api';
import { getRememberMe, setRememberMe, useAuthStore } from '@/store/authStore';
import { ENDPOINTS } from '@shared/constants';
import { browserSupportsWebAuthn,startAuthentication,type PublicKeyCredentialRequestOptionsJSON } from '@simplewebauthn/browser';
import { UserRole,type User } from '@shared/types';
import { Eye,EyeOff,Fingerprint,KeyRound,Loader2,Mail } from 'lucide-react';
import { useEffect,useState } from 'react';
import { useNavigate } from 'react-router';

type AuthResponse = {
  user: User;
  tokens: { accessToken: string; refreshToken: string; expiresIn: number };
};

type AuthUserDto = {
  id: string;
  email: string;
  name?: string;
  role?: string;
  emailVerified?: boolean;
  onboardingCompleted?: boolean;
  onboardingStep?: number;
};

type AuthResponseDto = {
  user: AuthUserDto;
  tokens: { accessToken: string; refreshToken: string; expiresIn: number };
};

type PasswordLoginResponseDto = AuthResponseDto | {
  mfaRequired: true;
  method: 'OTP';
  challengeId: string;
  expiresIn: number;
};

type LoginMethod = 'password' | 'otp' | 'magic-link' | 'passkey';
type OtpStep = 'email' | 'code';
type MagicLinkStep = 'email' | 'sent';

const USER_ROLES = new Set<string>(Object.values(UserRole));

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const getErrorMessage = (error: unknown, fallback: string): string => {
  if (error instanceof Error && error.message) {
    return error.message;
  }

  return isRecord(error) && typeof error.message === 'string' && error.message ? error.message : fallback;
};

const getErrorName = (error: unknown): string | undefined => {
  if (error instanceof Error) {
    return error.name;
  }

  return isRecord(error) && typeof error.name === 'string' ? error.name : undefined;
};

const toUserRole = (role: unknown): UserRole =>
  typeof role === 'string' && USER_ROLES.has(role) ? (role as UserRole) : UserRole.VIEWER;

const toAuthResponse = (response: AuthResponseDto): AuthResponse => ({
  user: {
    id: response.user.id,
    email: response.user.email,
    name: response.user.name ?? '',
    role: toUserRole(response.user.role),
    emailVerified: response.user.emailVerified,
    onboardingCompleted: response.user.onboardingCompleted,
    onboardingStep: response.user.onboardingStep,
  },
  tokens: response.tokens,
});

const isPasskeyRequestOptions = (value: unknown): value is PublicKeyCredentialRequestOptionsJSON =>
  isRecord(value) &&
  typeof value.challenge === 'string' &&
  (value.timeout === undefined || typeof value.timeout === 'number') &&
  (value.rpId === undefined || typeof value.rpId === 'string') &&
  (value.allowCredentials === undefined || Array.isArray(value.allowCredentials)) &&
  (value.userVerification === undefined || typeof value.userVerification === 'string') &&
  (value.hints === undefined || Array.isArray(value.hints));

const isPasswordMfaRequired = (value: unknown): value is Extract<PasswordLoginResponseDto, { mfaRequired: true }> =>
  isRecord(value) &&
  value.mfaRequired === true &&
  value.method === 'OTP' &&
  typeof value.challengeId === 'string';

export default function Login() {
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [loginMethod, setLoginMethod] = useState<LoginMethod>('password');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [passwordMfaChallengeId, setPasswordMfaChallengeId] = useState<string | null>(null);
  const [otpStep, setOtpStep] = useState<OtpStep>('email');
  const [magicLinkStep, setMagicLinkStep] = useState<MagicLinkStep>('email');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMeState] = useState(getRememberMe);
  const [restoringSession, setRestoringSession] = useState(true);
  const navigate = useNavigate();
  const login = useAuthStore((s) => s.login);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const accessToken = useAuthStore((s) => s.accessToken);

  useEffect(() => {
    let active = true;

    if (isAuthenticated && accessToken) {
      navigate('/', { replace: true });
      setRestoringSession(false);
      return () => {
        active = false;
      };
    }

    void api.restoreSession()
      .then((restored) => {
        if (active && restored) {
          navigate('/', { replace: true });
        }
      })
      .finally(() => {
        if (active) {
          setRestoringSession(false);
        }
      });

    return () => {
      active = false;
    };
  }, [accessToken, isAuthenticated, navigate]);

  const handleRememberMeChange = (checked: boolean) => {
    setRememberMeState(checked);
    // Persist immediately so flows that finish on another page
    // (magic link verification) honor the choice too.
    setRememberMe(checked);
  };

  const rememberMeField = (id: string) => (
    <label
      htmlFor={id}
      className="flex cursor-pointer select-none items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
    >
      <input
        id={id}
        type="checkbox"
        checked={rememberMe}
        onChange={(e) => handleRememberMeChange(e.target.checked)}
        className="h-4 w-4 accent-primary"
      />
      Remember me on this device
    </label>
  );

  const supportsPasskeys = typeof window !== 'undefined' && browserSupportsWebAuthn();

  if (restoringSession) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const handleAuthSuccess = (data: AuthResponse) => {
    setRememberMe(rememberMe);
    login(data.user, data.tokens.accessToken);
    if (data.user.emailVerified === false) {
      navigate('/verify-email-pending', { replace: true });
    } else if (data.user.onboardingCompleted === false) {
      navigate('/onboarding', { replace: true });
    } else {
      navigate('/', { replace: true });
    }
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const endpoint = mode === 'login' ? ENDPOINTS.auth.login : ENDPOINTS.auth.register;
      const body = mode === 'login' ? { email, password, rememberMe } : { email, password, name };
      const data = await api.post<PasswordLoginResponseDto>(endpoint, body);
      if (mode === 'login' && isPasswordMfaRequired(data)) {
        setPasswordMfaChallengeId(data.challengeId);
        setLoginMethod('otp');
        setOtpStep('code');
        setOtpCode('');
        return;
      }
      if (isPasswordMfaRequired(data)) {
        throw new Error('Registration failed');
      }
      handleAuthSuccess(toAuthResponse(data));
    } catch (err: unknown) {
      setError(getErrorMessage(err, mode === 'login' ? 'Login failed' : 'Registration failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleOtpRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      await api.post(ENDPOINTS.auth.otpRequest, { email });
      setOtpStep('code');
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Failed to send code'));
    } finally {
      setLoading(false);
    }
  };

  const handleOtpVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const data = await api.post<AuthResponseDto>(ENDPOINTS.auth.otpVerify, {
        email,
        code: otpCode,
        rememberMe,
        ...(passwordMfaChallengeId ? { challengeId: passwordMfaChallengeId } : {}),
      });
      handleAuthSuccess(toAuthResponse(data));
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Invalid code'));
    } finally {
      setLoading(false);
    }
  };

  const handleMagicLinkRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      await api.post(ENDPOINTS.auth.magicLinkRequest, { email });
      setMagicLinkStep('sent');
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Failed to send link'));
    } finally {
      setLoading(false);
    }
  };

  const handlePasskeyLogin = async () => {
    setError(null);
    setLoading(true);

    try {
      if (!email.trim()) {
        setError('Enter your email address before using a passkey');
        return;
      }

      const optionsRes = await api.post<{ success: boolean; data: unknown }>(
        ENDPOINTS.auth.passkeyAuthOptions,
        { email: email.trim() }
      );
      if (!isPasskeyRequestOptions(optionsRes.data)) {
        throw new Error('Invalid passkey authentication options.');
      }
      const authResp = await startAuthentication({ optionsJSON: optionsRes.data });
      const data = await api.post<AuthResponseDto>(ENDPOINTS.auth.passkeyAuthVerify, { response: authResp, rememberMe });
      handleAuthSuccess(toAuthResponse(data));
    } catch (err: unknown) {
      if (getErrorName(err) === 'NotAllowedError') {
        setError('Passkey authentication was cancelled');
      } else {
        setError(getErrorMessage(err, 'Passkey login failed'));
      }
    } finally {
      setLoading(false);
    }
  };

  const resetToPassword = () => {
    setLoginMethod('password');
    setOtpStep('email');
    setMagicLinkStep('email');
    setOtpCode('');
    setPasswordMfaChallengeId(null);
    setError(null);
  };

  const toggleMode = () => {
    setMode(mode === 'login' ? 'register' : 'login');
    resetToPassword();
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <div className="w-full max-w-sm">
        {/* Logo / Brand */}
        <div className="mb-8 text-center">
          <img
            src="/favicon.png"
            alt="StockForge"
            className="mx-auto mb-4 h-16 w-16 object-cover shadow-[0_0_24px_rgba(20,184,166,0.22)]"
          />
          <h1
            className="text-4xl font-bold"
            style={{ fontFamily: "'Outfit', sans-serif", letterSpacing: '-0.03em' }}
          >
            <span style={{ color: 'hsl(var(--primary))' }}>StockForge</span>
          </h1>
          <p
            className="mt-2 text-muted-foreground text-xs uppercase"
            style={{ fontFamily: "'DM Mono', monospace", letterSpacing: '0.08em' }}
          >
            Manufacturing Intelligence
          </p>
        </div>

        {/* Auth Card */}
        <div className="forge-panel p-6">
          <h2
            className="mb-6 text-lg font-semibold uppercase"
            style={{ fontFamily: "'DM Mono', monospace", letterSpacing: '0.06em' }}
          >
            {mode === 'login' ? 'Sign In' : 'Create Account'}
          </h2>

          {error && (
            <div role="alert" className="mb-4 border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
              {error}
            </div>
          )}

          {/* Password Login / Register Form */}
          {(loginMethod === 'password' || mode === 'register') && (
            <form onSubmit={handlePasswordSubmit} className="space-y-4">
              {mode === 'register' && (
                <div>
                  <label htmlFor="name" className="label-industrial mb-1.5 block">Name</label>
                  <input
                    id="name" type="text" required autoComplete="name" autoFocus
                    value={name} onChange={(e) => setName(e.target.value)}
                    className="input-industrial w-full" placeholder="Full name"
                  />
                </div>
              )}

              <div>
                <label htmlFor="email" className="label-industrial mb-1.5 block">Email</label>
                <input
                  id="email" type="email" required autoComplete="email"
                  autoFocus={mode === 'login'}
                  value={email} onChange={(e) => setEmail(e.target.value)}
                  className="input-industrial w-full" placeholder="operator@example.com"
                />
              </div>

              <div>
                <label htmlFor="password" className="label-industrial mb-1.5 block">Password</label>
                <div className="relative">
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    required
                    autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                    minLength={mode === 'register' ? 12 : undefined}
                    value={password} onChange={(e) => setPassword(e.target.value)}
                    className="input-industrial w-full pr-10"
                    placeholder={mode === 'register' ? 'Min 12 characters' : 'Enter password'}
                  />
                  <button
                    type="button" onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground transition-colors"
                    tabIndex={-1} aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              {mode === 'login' && rememberMeField('remember-me-password')}

              <button
                type="submit"
                disabled={loading}
                data-testid="auth-submit"
                className="btn-industrial-primary w-full flex items-center justify-center gap-2"
              >
                {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                {loading
                  ? (mode === 'login' ? 'Authenticating...' : 'Creating account...')
                  : (mode === 'login' ? 'Sign In' : 'Create Account')
                }
              </button>
            </form>
          )}

          {/* OTP Login Flow */}
          {loginMethod === 'otp' && mode === 'login' && (
            <>
              {otpStep === 'email' && (
                <form onSubmit={handleOtpRequest} className="space-y-4">
                  <div>
                    <label htmlFor="otp-email" className="label-industrial mb-1.5 block">Email</label>
                    <input
                      id="otp-email" type="email" required autoComplete="email" autoFocus
                      value={email} onChange={(e) => setEmail(e.target.value)}
                      className="input-industrial w-full" placeholder="operator@example.com"
                    />
                  </div>
                  <button type="submit" disabled={loading} className="btn-industrial-primary w-full flex items-center justify-center gap-2">
                    {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                    {loading ? 'Sending code...' : 'Send Login Code'}
                  </button>
                </form>
              )}
              {otpStep === 'code' && (
                <form onSubmit={handleOtpVerify} className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    Enter the 6-digit code sent to <strong>{email}</strong>
                  </p>
                  <div>
                    <label htmlFor="otp-code" className="label-industrial mb-1.5 block">Code</label>
                    <input
                      id="otp-code" type="text" required autoFocus
                      maxLength={6} pattern="[0-9]{6}" inputMode="numeric"
                      value={otpCode} onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      className="input-industrial w-full text-center text-2xl tracking-[0.3em]"
                      style={{ fontFamily: "'DM Mono', monospace" }}
                      placeholder="000000"
                    />
                  </div>
                  {rememberMeField('remember-me-otp')}
                  <button type="submit" disabled={loading || otpCode.length !== 6} className="btn-industrial-primary w-full flex items-center justify-center gap-2">
                    {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                    {loading ? 'Verifying...' : 'Verify Code'}
                  </button>
                  <button type="button" onClick={() => {
                    setOtpStep('email');
                    setOtpCode('');
                    setPasswordMfaChallengeId(null);
                    setError(null);
                  }}
                    className="w-full text-sm text-muted-foreground hover:text-primary transition-colors"
                  >
                    Use a different email
                  </button>
                </form>
              )}
            </>
          )}

          {/* Magic Link Flow */}
          {loginMethod === 'magic-link' && mode === 'login' && (
            <>
              {magicLinkStep === 'email' && (
                <form onSubmit={handleMagicLinkRequest} className="space-y-4">
                  <div>
                    <label htmlFor="ml-email" className="label-industrial mb-1.5 block">Email</label>
                    <input
                      id="ml-email" type="email" required autoComplete="email" autoFocus
                      value={email} onChange={(e) => setEmail(e.target.value)}
                      className="input-industrial w-full" placeholder="operator@example.com"
                    />
                  </div>
                  {rememberMeField('remember-me-magic-link')}
                  <button type="submit" disabled={loading} className="btn-industrial-primary w-full flex items-center justify-center gap-2">
                    {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                    {loading ? 'Sending link...' : 'Send Sign-In Link'}
                  </button>
                </form>
              )}
              {magicLinkStep === 'sent' && (
                <div className="space-y-4 text-center">
                  <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
                    <Mail className="h-8 w-8 text-primary" />
                  </div>
                  <div>
                    <p className="font-medium">Check your email</p>
                    <p className="mt-1 text-sm text-muted-foreground">
                      We sent a sign-in link to <strong>{email}</strong>
                    </p>
                  </div>
                  <button type="button" onClick={() => { setMagicLinkStep('email'); setError(null); }}
                    className="text-sm text-muted-foreground hover:text-primary transition-colors"
                  >
                    Use a different email
                  </button>
                </div>
              )}
            </>
          )}

          {/* Alternative Login Methods (only in login mode) */}
          {mode === 'login' && loginMethod === 'password' && (
            <div className="mt-6">
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-border" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-card px-2 text-muted-foreground"
                    style={{ fontFamily: "'DM Mono', monospace", letterSpacing: '0.06em' }}
                  >
                    Or sign in with
                  </span>
                </div>
              </div>

              <div className="mt-4 grid gap-2">
                <button
                  type="button"
                  onClick={() => { setLoginMethod('otp'); setError(null); }}
                  className="btn-industrial-secondary flex w-full items-center justify-center gap-2 text-sm"
                >
                  <KeyRound className="h-4 w-4" />
                  Email me a code
                </button>
                <button
                  type="button"
                  onClick={() => { setLoginMethod('magic-link'); setError(null); }}
                  className="btn-industrial-secondary flex w-full items-center justify-center gap-2 text-sm"
                >
                  <Mail className="h-4 w-4" />
                  Email me a link
                </button>
                {supportsPasskeys && (
                  <button
                    type="button"
                    onClick={handlePasskeyLogin}
                    disabled={loading}
                    className="btn-industrial-secondary flex w-full items-center justify-center gap-2 text-sm"
                  >
                    <Fingerprint className="h-4 w-4" />
                    Use a passkey
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Back to password login */}
          {mode === 'login' && loginMethod !== 'password' && (
            <div className="mt-4 text-center">
              <button type="button" onClick={resetToPassword}
                className="text-sm text-muted-foreground hover:text-primary transition-colors"
                style={{ fontFamily: "'DM Mono', monospace" }}
              >
                Back to password login
              </button>
            </div>
          )}

          {/* Toggle login/register */}
          <div className="mt-4 text-center">
            <button
              type="button"
              onClick={toggleMode}
              data-testid="auth-toggle-mode"
              className="text-sm text-muted-foreground hover:text-primary transition-colors"
              style={{ fontFamily: "'DM Mono', monospace" }}
            >
              {mode === 'login' ? "Don't have an account? Register" : 'Already have an account? Sign in'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
